import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { getUser, logOut } from "./services/Auth/auth";
import { ThemeProvider } from "./components/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { isMarketingEnabled } from "./config";
import LandingPage from "./pages/LandingPage/LandingPage";

// Lazy-loaded views to keep initial bundle ultra-lightweight
const AuthenticatedWorkspace = lazy(() => import("./AuthenticatedWorkspace"));
const LoginPageInternal = lazy(() => import("./pages/LoginPage/LoginPage"));
const BookDemoPage = lazy(() => import("./pages/BookDemo/BookDemoPage"));
const GetQuotePage = lazy(() => import("./pages/GetQuote/GetQuotePage"));
const InvoicePage = lazy(() => import("./pages/Invoice/InvoicePage"));
const PaymentPage = lazy(() => import("./pages/Invoice/PaymentPage"));
const AdminBillingPage = lazy(() => import("./pages/Admin/AdminBillingPage"));

const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
    }}
  >
    <div
      style={{
        width: "32px",
        height: "32px",
        border: "3px solid rgba(124, 58, 237, 0.2)",
        borderTopColor: "#7c3aed",
        borderRadius: "50%",
        animation: "tfl-spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes tfl-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const getSystemTheme = () => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const getEffectiveTheme = (mode) => {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
};

const App = () => {
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null);

  const [colorMode, setColorMode] = useState(() => {
    const savedMode = localStorage.getItem("colorMode");
    return savedMode || "system";
  });

  const [effectiveTheme, setEffectiveTheme] = useState(() => getEffectiveTheme(colorMode));

  const setThemeMode = (mode) => {
    const validModes = ["SYSTEM", "LIGHT", "DARK"];
    const normalizedMode = mode.toUpperCase();

    if (validModes.includes(normalizedMode)) {
      setColorMode(normalizedMode.toLowerCase());
    } else {
      console.warn(`Invalid theme mode: ${mode}. Valid options are: SYSTEM, LIGHT, DARK`);
    }
  };

  useEffect(() => {
    checkAuthState();
  }, []);

  useEffect(() => {
    const newEffectiveTheme = getEffectiveTheme(colorMode);
    setEffectiveTheme(newEffectiveTheme);
    localStorage.setItem("colorMode", colorMode);

    if (newEffectiveTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = () => {
      if (colorMode === "system") {
        const updatedEffectiveTheme = getEffectiveTheme(colorMode);
        setEffectiveTheme(updatedEffectiveTheme);

        if (updatedEffectiveTheme === "dark") {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      }
    };

    if (colorMode === "system") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
    }

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [colorMode]);

  const checkAuthState = async () => {
    setLoading(true);
    try {
      const user = await getUser();
      setAuthUser(user);
    } catch {
      setAuthUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = useCallback(async () => {
    setLoading(true);
    try {
      await logOut();
      setAuthUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const isRootRoute = location.pathname === "/";
  const isLoginRoute =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/register";
  const isBookDemoRoute =
    location.pathname === "/book-demo" ||
    location.pathname === "/demo" ||
    location.pathname === "/schedule";
  const isGetQuoteRoute =
    location.pathname === "/get-quote" ||
    location.pathname === "/quote" ||
    location.pathname === "/pricing/quote";
  const isInvoiceRoute = /^\/invoice\/[^/]+$/.test(location.pathname);
  const isPaymentRoute = /^\/pay\/[^/]+$/.test(location.pathname);
  const isAdminBillingRoute = location.pathname === "/admin/billing";
  const marketingEnabled = isMarketingEnabled();

  const showMarketingLanding = !loading && !authUser && isRootRoute && marketingEnabled;
  const showBookDemoPage = !loading && isBookDemoRoute;
  const showGetQuotePage = !loading && isGetQuoteRoute;
  const showInvoicePage = !loading && isInvoiceRoute;
  const showPaymentPage = !loading && isPaymentRoute;
  const showAdminBillingPage = !loading && isAdminBillingRoute;
  const showLoginForm =
    !loading &&
    !authUser &&
    !isBookDemoRoute &&
    !isGetQuoteRoute &&
    !isInvoiceRoute &&
    !isPaymentRoute &&
    !isAdminBillingRoute &&
    (isLoginRoute || !marketingEnabled || !isRootRoute);

  useEffect(() => {
    if (authUser && isLoginRoute) {
      navigate("/");
    }
  }, [authUser, isLoginRoute, navigate]);

  useEffect(() => {
    if (
      !authUser &&
      marketingEnabled &&
      !isRootRoute &&
      !isLoginRoute &&
      !isBookDemoRoute &&
      !isGetQuoteRoute &&
      !isInvoiceRoute &&
      !isPaymentRoute &&
      !isAdminBillingRoute &&
      !loading
    ) {
      navigate("/");
    }
  }, [
    authUser,
    marketingEnabled,
    isRootRoute,
    isLoginRoute,
    isBookDemoRoute,
    isGetQuoteRoute,
    isInvoiceRoute,
    isPaymentRoute,
    isAdminBillingRoute,
    loading,
    navigate,
  ]);

  return (
    <div>
      <ThemeProvider
        colorMode={colorMode}
        effectiveTheme={effectiveTheme}
        setThemeMode={setThemeMode}
      >
        <Suspense fallback={<LoadingFallback />}>
          {showGetQuotePage ? (
            <GetQuotePage />
          ) : showBookDemoPage ? (
            <BookDemoPage />
          ) : showInvoicePage ? (
            <InvoicePage />
          ) : showPaymentPage ? (
            <PaymentPage />
          ) : showAdminBillingPage ? (
            <AdminBillingPage />
          ) : showMarketingLanding ? (
            <LandingPage />
          ) : loading ? (
            <LoadingFallback />
          ) : authUser ? (
            <AuthenticatedWorkspace
              user={authUser}
              colorMode={colorMode}
              effectiveTheme={effectiveTheme}
              setThemeMode={setThemeMode}
              onLogout={handleLogout}
            />
          ) : showLoginForm ? (
            <LoginPageInternal setAuthUser={checkAuthState} />
          ) : (
            <LandingPage />
          )}
        </Suspense>
      </ThemeProvider>
    </div>
  );
};

export default App;
